import React, { useEffect, useState } from 'react';
import aiService from '../../services/ai.service';
import { AiPlan, AiQuota } from '../../types/ai.types';
import './AiQuotaUpgrade.scss';

interface AiQuotaUpgradeProps {
  storeId: number;
  onUpgraded?: (quota: AiQuota) => void;
}

function tokenLabel(value: number): string {
  if (value >= 1000000) {
    const millions = value / 1000000;
    return `${Number.isInteger(millions) ? millions : millions.toFixed(1)} M`;
  }
  if (value >= 1000) return `${Math.round(value / 1000)} k`;
  return String(value);
}

const AiQuotaUpgrade: React.FC<AiQuotaUpgradeProps> = ({ storeId, onUpgraded }) => {
  const [plans, setPlans] = useState<AiPlan[]>([]);
  const [picked, setPicked] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    aiService.getPlans(storeId).then((data) => {
      if (cancelled) return;
      const next = data.plans.filter((plan) => !plan.current);
      setPlans(data.plans);
      setPicked(next[0]?.key || '');
      setLoaded(true);
    }).catch(() => {
      if (!cancelled) {
        setError('Paliers indisponibles');
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const selected = plans.find((plan) => plan.key === picked);
  const choices = plans.filter((plan) => !plan.current);
  if (!loaded) return <p className="ai-upgrade__label">Simulation</p>;
  if (!choices.length && !done) return null;

  const confirm = async () => {
    if (!picked || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await aiService.checkout(storeId, picked);
      setDone(result.quota.label);
      onUpgraded?.(result.quota);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
      setError(axiosErr?.response?.data?.error?.message || 'Simulation impossible');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return <p className="ai-upgrade__done">{done} activé</p>;
  }

  return (
    <div className="ai-upgrade">
      <p className="ai-upgrade__label">Simulation</p>
      <div className="ai-upgrade__plans" role="group" aria-label="Palier">
        {choices.map((plan) => (
          <button
            key={plan.key}
            type="button"
            className={plan.key === picked ? 'is-on' : ''}
            aria-pressed={plan.key === picked}
            disabled={busy}
            onClick={() => setPicked(plan.key)}
          >
            {plan.label}
            <span>{tokenLabel(plan.monthly_token_quota)}</span>
          </button>
        ))}
      </div>
      {selected && (
        <button type="button" className="ai-upgrade__pay" disabled={busy} onClick={() => void confirm()}>
          {busy ? '…' : `Simuler · ${selected.amount_mad} MAD`}
        </button>
      )}
      {error ? <p className="ai-upgrade__error">{error}</p> : null}
    </div>
  );
};

export default AiQuotaUpgrade;
